// ─── Search Context ───────────────────────────────────────────────────────────
// Provides route search state + handlers to all components via useSearch().
// Wraps useRouteSearch hook internally and handles cross-cutting concerns
// between search and trip (like "add route and continue").

import { createContext, useContext, useCallback, useState, useMemo, useEffect, useRef, type ReactNode } from "react";
import { DEFAULT_EXPLORE_FILTERS, trainRouteToTripLeg, type City, type TrainRoute, type ViewMode, type ExploreFilters, type StopActions } from "../data/types";
import { useRouteSearch, type RouteSearchState, type RouteSearchActions } from "../hooks/useRouteSearch";
import { useTrip } from "./TripContext";
import { calendarDaysBetween, nextDeparture, stationDateTime } from "../services/journeyTime";
import { getCityById } from "../services/cityStore";
import { toast } from "sonner";

// ─── Context Shape ──────────────────────────────────────────────────────

export interface SearchContextValue extends RouteSearchState, RouteSearchActions {
  viewMode: ViewMode;
  setViewMode: React.Dispatch<React.SetStateAction<ViewMode>>;
  exploreFilters: ExploreFilters;
  setExploreFilters: React.Dispatch<React.SetStateAction<ExploreFilters>>;
  mapControls: { zoomIn: () => void; zoomOut: () => void } | null;
  setMapControls: React.Dispatch<React.SetStateAction<{ zoomIn: () => void; zoomOut: () => void } | null>>;

  /** Route day offset (continues from trip day segments) */
  routeStartDay: number;

  // Composed cross-cutting handlers
  handleAddRouteAndContinue: (route: TrainRoute) => void;
  handleAddRouteToTripFromMap: (route: TrainRoute) => void;
}

const SearchContext = createContext<SearchContextValue | null>(null);

function getResultEndpoints(route: TrainRoute, endpoints: RouteSearchState["resultEndpoints"]) {
  const from = endpoints?.from ?? route.stationData?.find(s => s.id === route.from) ?? getCityById(route.from);
  const to = endpoints?.to ?? route.stationData?.find(s => s.id === route.to) ?? getCityById(route.to);
  return from && to ? { from, to } : null;
}

// ─── Provider ───────────────────────────────────────────────────────────

export function SearchProvider({ children }: Readonly<{ children: ReactNode }>) {
  const tripCtx = useTrip();
  const [viewMode, setViewMode] = useState<ViewMode>("search");
  const [exploreFilters, setExploreFilters] = useState<ExploreFilters>(DEFAULT_EXPLORE_FILTERS);
  const [mapControls, setMapControls] = useState<{ zoomIn: () => void; zoomOut: () => void } | null>(null);

  const search = useRouteSearch(tripCtx.travelDate, setViewMode);

  // ─── Stop-actions cleanup ─────────────────────────────────────

  /** Functional updater: keep only trip-stop actions (+ optional extra IDs) */
  const filterStopActions = useCallback(
    (extraIds: string[] = []) => (prev: StopActions): StopActions => {
      const keepIds = new Set(tripCtx.tripStops.map((s) => s.id));
      for (const id of extraIds) keepIds.add(id);
      const kept: StopActions = {};
      for (const [id, actions] of Object.entries(prev)) {
        if (keepIds.has(id)) kept[id] = actions;
      }
      return kept;
    },
    [tripCtx.tripStops]
  );

  // ─── Wrapped handlers (trip-state side effects) ───────────────────

  const handleSearch = useCallback(
    async (from?: City, to?: City, options?: { customDate?: string; customTime?: string }) => {
      tripCtx.setSelectedStop(null);
      // A split stop may have been added in this same event, before tripStops re-renders.
      tripCtx.setStopActions(filterStopActions([from?.id, to?.id].filter((id): id is string => !!id)));
      return search.handleSearch(from, to, options);
    },
    [search.handleSearch, tripCtx.setSelectedStop, tripCtx.setStopActions, filterStopActions]
  );

  const handleSelectRoute = useCallback(
    (index: number) => {
      search.handleSelectRoute(index);
      tripCtx.setSelectedStop(null);
    },
    [search.handleSelectRoute, tripCtx.setSelectedStop]
  );

  const handleReset = useCallback(
    (...args: [City] | [unknown?]) => {
      const isFullReset = !(args.length === 1 && typeof args[0] === "object" && args[0] && "latitude" in args[0]);
      search.handleReset(...args);
      tripCtx.setSelectedStop(null);
      if (isFullReset) tripCtx.setStopActions(filterStopActions());
    },
    [search.handleReset, tripCtx.setSelectedStop, tripCtx.setStopActions, filterStopActions]
  );

  // ─── Route day offset ─────────────────────────────────────────

  const activeRoute = search.routes[search.activeRouteIndex] || null;

  const routeStartDay = useMemo(() => {
    return activeRoute?.departureDate ? Math.max(1, calendarDaysBetween(tripCtx.travelDate, activeRoute.departureDate) + 1) : 1;
  }, [tripCtx.travelDate, activeRoute]);

  // ─── Auto-clear stale search routes on trip stop removal ─────
  // When a trip stop is removed whose station ID appears in the current
  // search route endpoints, clear the search results so the map doesn't
  // render lingering polylines to/from the removed stop.
  const prevTripStopIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const currentIds = new Set(tripCtx.tripStops.map((s) => s.id));
    const prevIds = prevTripStopIdsRef.current;
    prevTripStopIdsRef.current = currentIds;

    // Only act when stops were removed (not on initial mount or additions)
    if (prevIds.size === 0 || currentIds.size >= prevIds.size) return;
    if (search.routes.length === 0) return;

    // Check if any search route endpoint was removed from the trip
    const firstRoute = search.routes[0];
    const searchFrom = firstRoute.from;
    const searchTo = firstRoute.to;

    const fromRemoved = prevIds.has(searchFrom) && !currentIds.has(searchFrom);
    const toRemoved = prevIds.has(searchTo) && !currentIds.has(searchTo);

    // Also check station IDs from the route's station list (HAFAS IDs may
    // differ from trip stop IDs, so check both endpoints and all stations)
    const stationRemoved = firstRoute.stations.some(
      (sid) => prevIds.has(sid) && !currentIds.has(sid)
    );

    if (fromRemoved || toRemoved || stationRemoved) {
      search.clearSearchResults();
    }
  }, [tripCtx.tripStops, search.routes, search.clearSearchResults]);

  // ─── Cross-cutting handlers ───────────────────────────────────

  const handleAddRouteAndContinue = useCallback(
    (route: TrainRoute) => {
      const endpoints = getResultEndpoints(route, search.resultEndpoints);
      if (!endpoints) return;
      const { from: fromStation, to: destStation } = endpoints;

      tripCtx.handleAddRouteToTrip(
        route,
        fromStation,
        fromStation.id,
        destStation.id
      );

      tripCtx.handleContinueFrom(destStation, fromStation);

      // Clean stopActions — include from/dest since tripStops hasn't updated yet
      const extraIds = [fromStation.id, destStation.id];
      tripCtx.setStopActions(filterStopActions(extraIds));

      search.handleReset(destStation);
      search.clearSearchResults();
      const onward = stationDateTime(nextDeparture(destStation, trainRouteToTripLeg(route, {
        fromStationId: fromStation.id, toStationId: destStation.id,
      }), tripCtx.stopActions, tripCtx.travelDate), destStation);
      if (onward.date) search.setSearchDate(onward.date);
      if (onward.time) search.setDepartureTime(onward.time);
      toast.success(`Train to ${destStation.city} added to your itinerary`);
    },
    [tripCtx, search, filterStopActions]
  );

  // Map and result-list acceptance share one itinerary transition.
  const handleAddRouteToTripFromMap = handleAddRouteAndContinue;

  // ─── Context value ────────────────────────────────────────────

  const value: SearchContextValue = useMemo(() => ({
    // Route search state + wrapped handlers
    ...search,
    handleSearch,
    handleSelectRoute,
    handleReset,

    // View mode + explore
    viewMode,
    setViewMode,
    exploreFilters,
    setExploreFilters,
    mapControls,
    setMapControls,
    routeStartDay,

    // Composed handlers
    handleAddRouteAndContinue,
    handleAddRouteToTripFromMap,
  }), [
    search, handleSearch, handleSelectRoute, handleReset,
    viewMode, exploreFilters, mapControls, routeStartDay,
    handleAddRouteAndContinue, handleAddRouteToTripFromMap,
  ]);

  return <SearchContext.Provider value={value}>{children}</SearchContext.Provider>;
}

// ─── Hook ───────────────────────────────────────────────────────────────

export function useSearch(): SearchContextValue {
  const ctx = useContext(SearchContext);
  if (!ctx) throw new Error("useSearch must be used within <SearchProvider>");
  return ctx;
}
